import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreIcon } from '@/components/icons';

export interface DropdownAction {
  label: string;
  icon?: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface ActionDropdownProps {
  actions: DropdownAction[];
  id: string;
  className?: string;
}

export function ActionDropdown({ actions, className = '' }: ActionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuDirection, setMenuDirection] = useState<'up' | 'down'>('down');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? 192;
    const menuHeight = menuRef.current?.offsetHeight ?? 220;
    const gap = 8;
    const viewportPadding = 8;

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const shouldOpenUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    const top = shouldOpenUp
      ? Math.max(viewportPadding, triggerRect.top - menuHeight - gap)
      : Math.min(window.innerHeight - menuHeight - viewportPadding, triggerRect.bottom + gap);

    const alignedLeft = triggerRect.right - menuWidth;
    const left = Math.max(
      viewportPadding,
      Math.min(alignedLeft, window.innerWidth - menuWidth - viewportPadding)
    );

    setMenuDirection(shouldOpenUp ? 'up' : 'down');
    setMenuPosition({ top, left });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTrigger = !!triggerRef.current && triggerRef.current.contains(target);
      const clickedMenu = !!menuRef.current && menuRef.current.contains(target);

      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', updateMenuPosition, true);
      updateMenuPosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, actions.length, updateMenuPosition]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action: DropdownAction, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    action.onClick(e);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        ref={triggerRef}
        className="text-gray-500 hover:text-gray-800 p-2"
        onClick={handleToggle}
        title="Actions"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <MoreIcon className="w-5 h-5" />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[1100]"
          style={{ top: menuPosition.top, left: menuPosition.left }}
          data-direction={menuDirection}
        >
          <p className='px-4 text-left text-sm font-bold items-center border-b py-2'>Menu</p>
          {actions.map((action, index) => (
            <button
              key={`${action.label}-${index}`}
              onClick={(e) => handleActionClick(action, e)}
              disabled={action.disabled}
              className={`
                w-full text-left px-4 py-2 text-sm flex items-center gap-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${action.variant === 'danger'
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-gray-700 hover:bg-gray-50'
                }
              `}
            >
              {action.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default ActionDropdown;
